import { useState, useEffect } from 'react';
import { getAvatarUrl, getDefaultAvatarUrl } from '@/lib/avatars';
import { Link, useNavigate, useNavigationType, useLocation } from 'react-router-dom';

const MENU_NAV_KEY = 'seyu_from_menu';
import { Bell, Settings, User, HelpCircle, LogOut, Sparkles, Zap, Compass, Users, UserPlus, ExternalLink, X, Shirt, ShoppingBag, Vote, Chrome, Play, RefreshCw, MessageSquareHeart, Newspaper, Copy, Wand2, Rocket, DollarSign } from 'lucide-react';
import { OnboardingProgress } from '@/components/activation/OnboardingProgress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useScrollHide } from '@/hooks/useScrollHide';
import { useNotifications } from '@/hooks/useNotifications';
import { useDevMode } from '@/contexts/DevModeContext';
import { useUniversalCredits } from '@/hooks/useUniversalCredits';
import { useTryOnJobs, onTryOnJobComplete, TryOnJob } from '@/hooks/useTryOnJobs';
import { usePromoCards } from '@/hooks/usePromoCards';
import { useAppUpdates } from '@/hooks/useAppUpdates';
import { useOtaUpdate } from '@/hooks/useOtaUpdate';
import { useFriendFeedUnread } from '@/hooks/useFriendFeedUnread';
import { cn } from '@/lib/utils';
import logo from '@/assets/want-w-icon.svg';
import wordmark from '@/assets/wntd-wordmark.svg';

import {
  Sheet,
  SheetContent,
  SheetTrigger } from
'@/components/ui/sheet';
// BuyCreditsSheet removed - app is free during early adoption
import { AuthDialog } from '@/components/auth/AuthDialog';
import { ReferralShareSection } from '@/components/referral/ReferralShareSection';
import { FindUsersSheet } from '@/components/users/FindUsersSheet';
import { ChatDemoSheet } from '@/components/activation/ChatDemoSheet';
import { HowToSaveSheet } from '@/components/activation/HowToSaveSheet';
import { FeedbackSheet } from '@/components/feedback/FeedbackSheet';
import { toast } from 'sonner';
import { InlineWhatsNewCard } from '@/components/layout/InlineWhatsNewCard';
import { triggerHaptic } from '@/hooks/useHaptics';
import { BUILD_VERSION } from '@/constants/buildVersion';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';


interface HeaderProps {
  title?: string;
  showAvatar?: boolean;
  showNotification?: boolean;
  leftContent?: React.ReactNode;
  afterAvatarContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  onRefresh?: () => Promise<void> | void;
  /** Use a shorter gradient when there are no segmented controls below the header */
  compactGradient?: boolean;
  /** Hide the gradient overlay entirely (e.g. on empty states) */
  noGradient?: boolean;
  /** Show a centered page title instead of the logo bubble */
  titleCenter?: string;
}


interface MenuContentProps {
  user: ReturnType<typeof useAuth>['user'];
  profile: ReturnType<typeof useProfile>['profile'];
  isMaxTier: boolean;
  onSignOut: () => void;
  onSignIn: () => void;
  onCloseMenu: () => void;
  onOpenChatDemo: () => void;
  onOpenHowToSave: () => void;
  onNavigateFromMenu: (path: string) => void;
  whatsNewUnreadCount: number;
  whatsNewLatestUpdate: ReturnType<typeof useAppUpdates>['updates'][0] | undefined;
  whatsNewReadIds: string[];
  onOpenFindUsers: () => void;
  onOpenFeedback: () => void;
  hasFriendFeedUnread: boolean;
  onMarkFriendFeedViewed: () => void;
}

function MenuContent({ user, profile, isMaxTier, onSignOut, onSignIn, onCloseMenu, onOpenChatDemo, onOpenHowToSave, onNavigateFromMenu, whatsNewUnreadCount, whatsNewLatestUpdate, whatsNewReadIds, onOpenFindUsers, onOpenFeedback, hasFriendFeedUnread, onMarkFriendFeedViewed }: MenuContentProps) {
  const [dismissedCards, setDismissedCards] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const stored = localStorage.getItem('dismissed-promo-cards');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const { data: promoCards } = usePromoCards();
  const { isEnabled } = useFeatureFlags();


  const handleDismissPromo = (e: React.MouseEvent, cardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const newDismissed = new Set(dismissedCards).add(cardId);
    setDismissedCards(newDismissed);
    localStorage.setItem('dismissed-promo-cards', JSON.stringify([...newDismissed]));
  };

  const handleShareApp = async () => {
    // Environment-aware domain for sharing
    const appDomain = import.meta.env.MODE === 'production' ?
    'https://wntd.ai' :
    import.meta.env.MODE === 'staging' ?
    'https://wntd.ai' :
    'https://seyu.lovable.app';

    const shareData = {
      title: 'Check out this app!',
      text: 'I\'ve been using this amazing fashion app - you should try it!',
      url: appDomain
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(appDomain);
        // Copied silently
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'Guest';
  const avatarUrl = getAvatarUrl(profile?.avatar_url, user?.id || 'guest');

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    if (hour < 21) return 'Evening';
    return 'Night';
  };
  const greeting = getGreeting();
  // Filter out dismissed cards
  const visiblePromoCards = promoCards?.filter((card) => !dismissedCards.has(card.id)) || [];

  return (
    <>
    <SheetContent side="left" className="w-72 p-0 bg-background border-r border-border/50 overflow-y-auto pt-[env(safe-area-inset-top)]">
      {/* User info header */}
      <div className="flex items-center gap-3 px-5 py-6 border-b border-border/30">
        {user ?
          <>
            <Link to="/profile" className="relative block">
              <img
                src={avatarUrl}
                alt="Profile"
                className="w-14 h-14 rounded-full object-cover ring-2 ring-border/30" />

            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-foreground truncate">{greeting}, {displayName}</p>
              {profile?.handle &&
              <span className="text-sm text-muted-foreground truncate">@{profile.handle}</span>
              }
            </div>
          </> :

          <div className="flex flex-col items-center w-full gap-2 py-2">
            <img src={wordmark} alt="WNTD" className="h-8" />
            <p className="text-lg font-bold text-foreground tracking-tight">See yourself, differently</p>
            <p className="text-xs text-muted-foreground text-center leading-relaxed">Save, try on, and discover 
            </p>
          </div>}
      </div>
      
      {user &&
        <>
          {/* CMS-Managed Promo Cards */}
          {visiblePromoCards.length > 0 &&
          <div className="px-3 pt-4 space-y-3">
              {visiblePromoCards.map((card) =>
            <div
              key={card.id}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 border border-primary/20 hover:border-primary/40 transition-all duration-300 group">

                  <button
                onClick={(e) => handleDismissPromo(e, card.id)}
                className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                aria-label="Dismiss">

                    <X className="w-3.5 h-3.5" />
                  </button>
                  {card.link_url ?
              <a
                href={card.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block">

                      {card.image_url &&
                <div className="aspect-[2/1] relative overflow-hidden">
                          <img
                    src={card.image_url}
                    alt={card.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                        </div>
                }
                      <div className="p-3">
                        {card.badge_text &&
                  <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs font-semibold text-primary uppercase tracking-wider">{card.badge_text}</span>
                          </div>
                  }
                        <p className="text-sm font-semibold text-foreground mb-0.5">{card.title}</p>
                        <p className="text-xs text-muted-foreground">{card.description}</p>
                        {card.cta_text &&
                  <div className="flex items-center gap-1 mt-2 text-xs text-primary font-medium">
                            <span>{card.cta_text}</span>
                            <ExternalLink className="w-3 h-3" />
                          </div>
                  }
                      </div>
                    </a> :

              <>
                      {card.image_url &&
                <div className="aspect-[2/1] relative overflow-hidden">
                          <img
                    src={card.image_url}
                    alt={card.title}
                    className="w-full h-full object-cover" />

                        </div>
                }
                      <div className="p-3">
                        {card.badge_text &&
                  <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs font-semibold text-primary uppercase tracking-wider">{card.badge_text}</span>
                          </div>
                  }
                        <p className="text-sm font-semibold text-foreground mb-0.5">{card.title}</p>
                        <p className="text-xs text-muted-foreground">{card.description}</p>
                      </div>
                    </>
              }
                </div>
            )}
            </div>
          }
        </>
        }

      {/* Feature highlights for logged-out users */}
      {!user &&
        <div className="px-3 py-4 space-y-1">
          <div className="flex items-center gap-4 px-4 py-3 rounded-xl">
            <ShoppingBag className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-foreground">Save Anything</p>
              <p className="text-xs text-muted-foreground">Paste a link or snap a pic, we make it shoppable</p>
            </div>
          </div>
          <div className="flex items-center gap-4 px-4 py-3 rounded-xl">
            <Shirt className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-foreground">Virtual Try-On</p>
              <p className="text-xs text-muted-foreground">See how clothes look on you with AI, create outfits </p>
            </div>
          </div>
          <div className="flex items-center gap-4 px-4 py-3 rounded-xl">
            <Vote className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-foreground">Style Polls</p>
              <p className="text-xs text-muted-foreground">Get opinions from friends and the community</p>
            </div>
          </div>
          <div className="flex items-center gap-4 px-4 py-3 rounded-xl">
            <Users className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-foreground">Friend Feed</p>
              <p className="text-xs text-muted-foreground">See what your friends are saving and shop together</p>
            </div>
          </div>
        </div>
        }

      {user &&
        <div className="py-3 px-3 pb-20">
          {/* Onboarding Progress Tracker */}
          <OnboardingProgress
            onNavigate={onNavigateFromMenu}
            onOpenFindUsers={onOpenFindUsers}
            onCloseMenu={onCloseMenu} />


          {/* Account Section - now includes My Polls and My Purchases */}
          <div className="mb-2">
            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</p>
            <button
              onClick={() => onNavigateFromMenu('/profile')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <User className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Profile</span>
            </button>
            <button
              onClick={() => onNavigateFromMenu('/settings')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <Settings className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Settings</span>
            </button>
          </div>

          {/* Style Studio section removed - now in bottom nav Studio tab */}

          {/* Discover Section */}
          <div className="mb-2">
            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Discover</p>
            
            {/* What's New - Inline Card */}
            {whatsNewLatestUpdate ?
            <InlineWhatsNewCard
              latestUpdate={whatsNewLatestUpdate}
              isUnread={whatsNewUnreadCount > 0 && !whatsNewReadIds.includes(whatsNewLatestUpdate.id)}
              totalUnread={whatsNewUnreadCount}
              onNavigate={onNavigateFromMenu} /> :


            <button
              onClick={() => onNavigateFromMenu('/whats-new')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

                <Newspaper className="w-5 h-5 text-muted-foreground" />
                <span className="flex-1 text-left">What's New</span>
              </button>
            }

            {/* Friend Feed */}
            <button
              onClick={() => {
                onMarkFriendFeedViewed();
                onNavigateFromMenu('/friends');
              }}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <div className="relative">
                <Users className="w-5 h-5 text-muted-foreground" />
                {hasFriendFeedUnread &&
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
                }
              </div>
              <span className="flex-1 text-left">Friend Feed</span>
            </button>

            {/* Add Friends */}
            <button
              onClick={() => {
                onCloseMenu();
                onOpenFindUsers();
              }}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <UserPlus className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Add Friends</span>
            </button>

            {/* Price Guess */}
            {isEnabled('price_guess') &&
            <button
              onClick={() => onNavigateFromMenu('/price-guess')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <DollarSign className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Price Guess</span>
            </button>
            }

            {/* Daily Outfit */}
            {isEnabled('daily_outfit') &&
            <button
              onClick={() => onNavigateFromMenu('/daily-outfit')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <Shirt className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Daily Outfit</span>
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-primary/10 text-primary uppercase">New</span>
            </button>
            }

          </div>


           {/* About WNTD Section */}
           <div className="mb-2">
             <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">About WNTD</p>
            <button
              onClick={onOpenChatDemo}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <Play className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">See Explainer</span>
            </button>
            <button
              onClick={() => onNavigateFromMenu('/about')}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <Rocket className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Our Manifesto</span>
            </button>
            <button
              onClick={onOpenFeedback}
              className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] text-foreground hover:bg-muted transition-colors duration-200 w-full">

              <MessageSquareHeart className="w-5 h-5 text-muted-foreground" />
              <span className="flex-1 text-left">Send Feedback</span>
            </button>
            {user &&
            <>
                <div className="border-t border-border/20 my-1" />
                <ReferralShareSection />
              </>
            }
          </div>

        </div>
        }
      

      {/* Sign in at bottom (only for logged-out users) */}
      {!user &&
        <div className="sticky bottom-0 bg-background p-4 border-t border-border/30 space-y-3">
          <button
            onClick={onSignIn}
            className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground text-base font-semibold active:scale-[0.98] transition-transform">

            Sign In
          </button>
          <button
            onClick={onSignIn}
            className="w-full py-3 rounded-2xl border border-border bg-background text-foreground text-sm font-medium flex items-center justify-center gap-2.5 active:scale-[0.98] transition-transform">

            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>
          <button
            onClick={onSignIn}
            className="w-full py-3 rounded-2xl border border-border bg-background text-foreground text-sm font-medium flex items-center justify-center gap-2.5 active:scale-[0.98] transition-transform">

            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            Continue with Apple
          </button>
          <p className="text-[11px] text-muted-foreground text-center">Free to use · No credit card needed</p>
        </div>
        }

    </SheetContent>
    </>);

}

export function Header({
  title,
  showAvatar = true,
  showNotification = true,
  leftContent,
  afterAvatarContent,
  rightContent,
  onRefresh,
  compactGradient = false,
  noGradient = false,
  titleCenter
}: HeaderProps) {
  const isVisible = useScrollHide(10, 500, ['/chat']);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { tier, isUnlimited: isMaxTier } = useUniversalCredits();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();

  useEffect(() => {
    const checkScroll = (e?: Event) => {
      if (window.scrollY > 0) {setHasScrolled(true);return;}
      // Use the event target if it's a scrollable element (captures inner overflow containers)
      const target = e?.target;
      if (target && target !== document && target instanceof HTMLElement) {
        setHasScrolled(target.scrollTop > 0);
        return;
      }
      // Fallback: find visible scroll container (the one whose parent tab is visible)
      const containers = document.querySelectorAll('.overflow-y-auto');
      for (const el of containers) {
        if (el instanceof HTMLElement && el.offsetParent !== null) {
          setHasScrolled(el.scrollTop > 0);
          return;
        }
      }
      setHasScrolled(false);
    };

    const onWindowScroll = () => checkScroll();
    const onDocScroll = (e: Event) => checkScroll(e);
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    document.addEventListener('scroll', onDocScroll, { passive: true, capture: true });
    // Delay initial check to let tab containers mount
    const raf = requestAnimationFrame(() => checkScroll());
    return () => {
      window.removeEventListener('scroll', onWindowScroll);
      document.removeEventListener('scroll', onDocScroll, { capture: true } as EventListenerOptions);
      cancelAnimationFrame(raf);
    };
  }, [location.pathname]);

  const { unreadCount } = useNotifications();
  const { unreadCount: whatsNewUnreadCount, updates: whatsNewUpdates, readUpdateIds: whatsNewReadIds } = useAppUpdates();
  const { isDownloading: isOtaDownloading, downloadPercent: otaPercent } = useOtaUpdate();
  const { hasUnread: hasFriendFeedUnread, markAsViewed: markFriendFeedViewed } = useFriendFeedUnread();
  const { isDevMode } = useDevMode();
  const { pendingCount, hasNewCompletion } = useTryOnJobs();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showChatDemo, setShowChatDemo] = useState(false);
  const [showHowToSave, setShowHowToSave] = useState(false);
  // showBuyCredits removed - app is free during early adoption
  const [showFindUsers, setShowFindUsers] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // OTA toast notifications handled by OtaUpdateNotifier in App.tsx (single instance)

  // Reopen menu when navigating back from a menu-originated page
  useEffect(() => {
    if (navigationType === 'POP') {
      const fromMenu = sessionStorage.getItem(MENU_NAV_KEY);
      if (fromMenu === 'true') {
        sessionStorage.removeItem(MENU_NAV_KEY);
        setIsMenuOpen(true);
      }
    }
  }, [navigationType, location.pathname]);

  // Handler for navigating from menu items - stores flag so back returns to menu
  const handleNavigateFromMenu = (path: string) => {
    sessionStorage.setItem(MENU_NAV_KEY, 'true');
    setIsMenuOpen(false);
    navigate(path);
  };

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    triggerHaptic('light');
    setIsRefreshing(true);
    try {
      await onRefresh();
      triggerHaptic('success');
    } finally {
      setIsRefreshing(false);
    }
  };

  const avatarUrl = profile?.avatar_url || (user ? getDefaultAvatarUrl(user.id) : null);

  // Listen for try-on job completions to show in-app notification
  useEffect(() => {
    const unsubscribe = onTryOnJobComplete((job: TryOnJob) => {
      // Skip toast if user is already on the try-on page (they'll see the result directly)
      if (window.location.pathname === '/try-on') {
        return;
      }

      // Animate the bell
      setBellAnimating(true);
      setTimeout(() => setBellAnimating(false), 2000);

      // Show in-app toast with the result
      const itemNames = job.clothing_items.slice(0, 2).map((i) => i.name).join(', ');
      toast(
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(`/app/try-on?look=${job.result_look_id || ''}`)}>

          {job.result_image_url &&
          <img
            src={job.result_image_url}
            alt="Try-on result"
            className="w-14 h-14 rounded-xl object-cover border border-border" />

          }
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Your look is ready! ✨</p>
            <p className="text-xs text-muted-foreground truncate">
              {itemNames || 'Tap to see how you look'}
            </p>
          </div>
          <Shirt className="w-5 h-5 text-primary shrink-0" />
        </div>,
        {
          id: `tryon-complete-${job.id}`,
          duration: 6000,
          action: {
            label: 'View',
            onClick: () => navigate(`/app/try-on?look=${job.result_look_id || ''}`)
          }
        }
      );
    });

    return () => {unsubscribe();};
  }, [navigate]);

  const handleSignOut = async () => {
    setIsMenuOpen(false); // Close menu first
    try {
      await signOut();
    } catch (error) {
      // AuthSessionMissingError is expected if no session exists - ignore it
      const isSessionMissing = (error as Error)?.message?.includes('session missing');
      if (!isSessionMissing) {
        console.error('Error signing out:', error);
      }
    }
    // Always navigate home, even if sign out "failed" due to no session
    navigate('/app');
  };

  const hasPendingJobs = pendingCount > 0;

  return (
    <>
    {/* Gradient overlay for legibility behind glass bubbles */}
    <div
        data-glass-exclude
        className="header-gradient-overlay fixed top-0 left-0 right-0 z-[15] pointer-events-none"
        style={{
          height: `calc(env(safe-area-inset-top, 0px) + ${compactGradient ? '80px' : '120px'})`,
          opacity: isVisible && hasScrolled && !noGradient ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }} />

    <header
        className={cn(
          "header-glass fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ease-out pt-[env(safe-area-inset-top)]",
          !isVisible && "-translate-y-full",
          isDevMode && "ring-2 ring-amber-500/50"
        )}
        style={{ pointerEvents: 'none' }}>

      {/* Try-On Progress Bar */}
      {hasPendingJobs &&
        <div className="h-1 w-full bg-surface-2 overflow-hidden" style={{ pointerEvents: 'auto' }}>
          <div
            className="h-full bg-gradient-to-r from-primary via-primary-dark to-primary tryon-progress-bar"
            style={{
              width: '100%',
              animation: 'progress-pulse 1.5s ease-in-out infinite, shimmer 2s linear infinite',
              backgroundSize: '200% 100%'
            }} />

        </div>
        }
      
      <div className="flex items-center justify-between px-4 py-3" style={{ pointerEvents: 'auto' }}>
        {/* Left cluster */}
        <div className="flex items-center gap-2 flex-1">
          {leftContent}
          {showAvatar &&
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <button className="glass-bubble w-10 h-10 focus:outline-none">
                  {user ?
                  <img
                    src={avatarUrl!}
                    alt="Profile"
                    className={cn(
                      "w-10 h-10 rounded-full object-cover",
                      isOtaDownloading && "ring-2 ring-primary/30"
                    )} /> :


                  <User className="w-5 h-5 header-glass-icon" />
                  }
                  {isOtaDownloading &&
                  <svg
                    className="absolute -inset-0.5 w-[42px] h-[42px]"
                    viewBox="0 0 42 42">

                      <circle
                      cx="21" cy="21" r="19"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="text-primary/20" />

                      <circle
                      cx="21" cy="21" r="19"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      className="text-primary"
                      strokeDasharray={`${2 * Math.PI * 19}`}
                      strokeDashoffset={`${2 * Math.PI * 19 * (1 - otaPercent / 100)}`}
                      transform="rotate(-90 21 21)"
                      style={{ transition: 'stroke-dashoffset 0.3s ease' }} />

                    </svg>
                  }
                </button>
              </SheetTrigger>
              <MenuContent
                user={user}
                profile={profile}
                isMaxTier={isMaxTier}
                onSignOut={handleSignOut}
                onSignIn={() => setShowAuthDialog(true)}
                onCloseMenu={() => setIsMenuOpen(false)}
                onOpenChatDemo={() => setShowChatDemo(true)}
                onOpenHowToSave={() => setShowHowToSave(true)}
                onNavigateFromMenu={handleNavigateFromMenu}
                whatsNewUnreadCount={whatsNewUnreadCount}
                whatsNewLatestUpdate={whatsNewUpdates[0]}
                whatsNewReadIds={whatsNewReadIds}
                onOpenFindUsers={() => setShowFindUsers(true)}
                onOpenFeedback={() => setShowFeedback(true)}
                hasFriendFeedUnread={hasFriendFeedUnread}
                onMarkFriendFeedViewed={markFriendFeedViewed} />

            </Sheet>
            }
          {afterAvatarContent}
        </div>

        {/* Center — page title or logo bubble */}
        <div className="absolute left-1/2 -translate-x-1/2">
          {titleCenter ?
            <span className="text-base font-semibold text-foreground whitespace-nowrap">{titleCenter}</span> :

            <Link to="/" className="glass-bubble w-10 h-10">
              <img src={logo} alt="Logo" className="h-5 w-auto logo-brand" />
            </Link>
            }
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          {onRefresh &&
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="glass-bubble w-10 h-10 disabled:opacity-50"
              aria-label="Refresh">

              <RefreshCw
                className={cn(
                  "w-[18px] h-[18px] header-glass-icon",
                  isRefreshing && "animate-spin"
                )}
                strokeWidth={2} />

            </button>
            }
          {rightContent}
          {user && showNotification &&
            <div className="glass-capsule">
              <Link
                to="/friends"
                onClick={() => markFriendFeedViewed()}
                className="relative p-2.5"
                aria-label="Friend Feed">

                <Users className="w-[18px] h-[18px] header-glass-icon" strokeWidth={2} />
                {hasFriendFeedUnread &&
                <span className="absolute top-1.5 right-0.5 w-2 h-2 rounded-full bg-primary animate-pulse" />
                }
              </Link>
              <Link
                to="/notifications"
                className={cn(
                  "relative p-2.5",
                  bellAnimating && "animate-bell-ring"
                )}>

                <Bell
                  className={cn(
                    "w-[18px] h-[18px] header-glass-icon",
                    (bellAnimating || hasNewCompletion) && "icon-active"
                  )}
                  strokeWidth={2} />

                {unreadCount > 0 &&
                <span className={cn(
                  "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-primary-foreground bg-primary rounded-full",
                  bellAnimating && "animate-pulse"
                )}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                }
                {bellAnimating &&
                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                }
              </Link>
            </div>
            }
        </div>
      </div>
    </header>
    
    <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    <ChatDemoSheet open={showChatDemo} onOpenChange={setShowChatDemo} />
    <HowToSaveSheet open={showHowToSave} onOpenChange={setShowHowToSave} />
    {/* BuyCreditsSheet removed - app is free during early adoption */}
    <FindUsersSheet open={showFindUsers} onOpenChange={setShowFindUsers} />
    <FeedbackSheet open={showFeedback} onOpenChange={setShowFeedback} />
    </>);

}